/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ChangeOptions, Change } from '@typescript-kit/core';
import { EntityFieldKey, EntityData, Entity, EntityModel } from '@typescript-kit/data';

export class UserFieldKey extends EntityFieldKey {
  static readonly ID = 'id';
  static readonly USER_NAME = 'userName';
  static readonly PASSWORD = 'password';
  static readonly NAME = 'name';
  static readonly EMAIL = 'email';
  static readonly HAS_ACCEPTED_TERMS_AND_CONDITIONS = 'hasAcceptedTermsAndConditions';
  static readonly IS_ACTIVE = 'isActive';
}

export interface UserData extends EntityData {
  id?: string;
  userName?: string;
  password?: string;
  name?: string;
  email?: string;
  hasAcceptedTermsAndConditions?: boolean;
  isActive?: boolean;
}

export class User extends Entity implements UserData {
  readonly id: string;
  readonly userName: string;
  readonly password: string;
  readonly name: string;
  readonly email: string;
  readonly hasAcceptedTermsAndConditions: boolean;
  readonly isActive: boolean;

  constructor(data: UserData) {
    super(data);
    this.id = data.id;
    this.userName = data.userName;
    this.password = data.password;
    this.name = data.name;
    this.email = data.email;
    this.hasAcceptedTermsAndConditions = data.hasAcceptedTermsAndConditions;
    this.isActive = data.isActive;
  }
}

export class UserModel extends EntityModel implements UserData {
  static readonly KEYS = EntityModel.KEYS.concat(
    Object.keys(UserFieldKey).map(key => UserFieldKey[key])
  );

  constructor(data: UserData) {
    super(data, UserModel.KEYS);
  }

  get id(): string {
    return this.get(UserFieldKey.ID);
  }

  set id(value: string) {
    this.set(UserFieldKey.ID, value);
  }

  get userName(): string {
    return this.get(UserFieldKey.USER_NAME);
  }

  set userName(value: string) {
    this.set(UserFieldKey.USER_NAME, value);
  }

  get password(): string {
    return this.get(UserFieldKey.PASSWORD);
  }

  set password(value: string) {
    this.set(UserFieldKey.PASSWORD, value);
  }

  get name(): string {
    return this.get(UserFieldKey.NAME);
  }

  set name(value: string) {
    this.set(UserFieldKey.NAME, value);
  }

  get email(): string {
    return this.get(UserFieldKey.EMAIL);
  }

  set email(value: string) {
    this.set(UserFieldKey.EMAIL, value);
  }

  get hasAcceptedTermsAndConditions(): boolean {
    return this.get(UserFieldKey.HAS_ACCEPTED_TERMS_AND_CONDITIONS);
  }

  set hasAcceptedTermsAndConditions(value: boolean) {
    this.set(UserFieldKey.HAS_ACCEPTED_TERMS_AND_CONDITIONS, value);
  }

  get isActive(): boolean {
    return this.get(UserFieldKey.IS_ACTIVE);
  }

  set isActive(value: boolean) {
    this.set(UserFieldKey.IS_ACTIVE, value);
  }

  setValues(values: UserData, options?: ChangeOptions): { [p: string]: Change<any> } {
    return super.setValues(values, options);
  }
}
